import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, ITextAreaProps, Modal, TextArea } from '@byte-design/ui';
import './index.scss';
import { IButtonProps } from '@byte-design/ui/lib/components/button/Button';
import classnames from 'classnames';
import { useLoadingEffect } from '../../../../utils/hooks/loading';

export interface TTCXModalProps {
  show?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onOk?: (text?: string) => void;
  okText?: string | React.ReactNode;
  onCancel?: (text?: string) => void;
  cancelText?: string | React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  hideToDestroy?: boolean;
  header?: null | React.ReactNode;
  content?: null | React.ReactNode;
  footer?: null | React.ReactNode;
  footerAlign?: 'vertical' | 'horizontal';
  hideOkButton?: boolean;
  hideCancelButton?: boolean;
  okButtonProps?: IButtonProps;
  cancelButtonProps?: IButtonProps;
  closable?: boolean;
  withInput?: boolean;
  inputConfig?: {
    defaultValue?: string;
    placeholder?: string;
    className?: string;
    maxLength?: number;
    required?: boolean;
    errorText?: string;
    inputProps?: ITextAreaProps;
    rules?: {
      validate: RegExp;
      errorText: string;
    }[];
    validateOnCancel?: boolean;
    validateOnOk?: boolean;
  };
}

export interface TTCXModalHandler {
  open: (config: TTCXModalProps) => void;
  close: () => void;
}

function TTCXModal(props: TTCXModalProps, ref: React.Ref<TTCXModalHandler>) {
  const [modalProps, setModalProps] = useState(props);
  const [initShow, setInitShow] = useState(true);
  const [inputValue, setInputValue] = useState<string | undefined>();
  const [errorText, setErrorText] = useState<string | undefined>();
  const isInit = useRef<boolean>(true);
  const {
    show: propsShow,
    size = 'sm',
    className,
    hideToDestroy = true,
    footerAlign = 'horizontal',
    onOk,
    okText,
    onCancel,
    cancelText,
    header,
    content,
    footer,
    hideOkButton = false,
    hideCancelButton = false,
    okButtonProps,
    cancelButtonProps,
    closable = false,
    withInput,
    inputConfig,
    wrapperClassName
  } = modalProps;
  const [show, setShow] = useState<boolean>(!!propsShow);

  function open(modalConfig: TTCXModalProps) {
    setModalProps(modalConfig);
    setShow(true);
  }

  function close() {
    setShow(false);
    setInputValue(undefined);
    isInit.current = true;
  }

  useImperativeHandle(ref, () => ({
    open,
    close
  }));

  async function onClickOk() {
    await onOk?.(inputValue);
  }

  const [okLoading, _onOk] = useLoadingEffect(onClickOk);

  async function onClickCancel() {
    await onCancel?.(inputValue);
  }

  const [cancelLoading, _onCancel] = useLoadingEffect(onClickCancel);

  function onInputChange(val?: string) {
    if (inputConfig?.maxLength) {
      setInputValue(val?.slice(0, inputConfig?.maxLength));
    } else {
      setInputValue(val);
    }
    isInit.current = false;
  }

  const hasError = useMemo(() => {
    let flag = false;
    if (inputConfig?.required) {
      flag = inputValue !== undefined ? inputValue.trim().length === 0 : true;
      if (flag) {
        setErrorText(inputConfig?.errorText);
        return flag;
      }
    }
    if (inputConfig?.rules) {
      inputConfig?.rules?.forEach((rule) => {
        flag = rule?.validate?.test(inputValue!);
        if (flag) {
          setErrorText(rule?.errorText);
          return flag;
        }
      });
    }
    return flag;
  }, [inputConfig, inputValue]);

  const okButton = useMemo(() => {
    const disabled = inputConfig?.validateOnOk && hasError;
    return (
      <Button type="primary" onClick={_onOk} disabled={disabled} {...okButtonProps} loading={okLoading}>
        {okText}
      </Button>
    );
  }, [onOk, okText, okButtonProps, inputConfig, hasError, okLoading, inputValue]);

  const cancelButton = useMemo(() => {
    const disabled = inputConfig?.validateOnCancel && hasError;
    return (
      <Button onClick={_onCancel} disabled={disabled} {...cancelButtonProps} loading={cancelLoading}>
        {cancelText}
      </Button>
    );
  }, [onCancel, cancelText, cancelButtonProps, inputConfig, hasError, cancelLoading, inputValue]);

  const modalFooter = useMemo(
    () =>
      footer !== undefined ? (
        footer
      ) : (
        <div className={`ttcx-modal-footer ttcx-footer-${footerAlign}-align`}>
          {!hideCancelButton && cancelButton}
          {!hideOkButton && okButton}
        </div>
      ),
    [
      footer,
      footerAlign,
      hideCancelButton,
      hideOkButton,
      onCancel,
      cancelText,
      cancelButtonProps,
      onOk,
      okText,
      okButtonProps,
      hasError,
      cancelLoading,
      okLoading,
      inputValue
    ]
  );

  useEffect(() => {
    withInput && setInputValue(inputConfig?.defaultValue);
  }, []);

  useEffect(() => {
    setModalProps(props);
    if (props.show !== undefined) {
      setShow(props.show);
    }
  }, [props]);

  // bui modal only can work after show one time
  useEffect(() => {
    setInitShow(false);
  }, []);

  return (
    <Modal
      wrapperClassName={`ttcx-modal-wrapper ${wrapperClassName || ''}`}
      show={initShow || show}
      header={header}
      hideToDestroy={hideToDestroy}
      className={`ttcx-modal ttcx-modal-${size}${!content ? ' ttcx-modal-no-content' : ''}${
        closable ? ' ttcx-modal-need-close' : ''
      }${className ? ` ${className}` : ''}`}
      okText={okText}
      cancelText={cancelText}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      footer={modalFooter}
      closeable={closable}
      onCancel={_onCancel}
    >
      {content ? (
        <>
          {content}
          {withInput && (
            <div
              className={classnames('ttcx-modal-input-wrapper', {
                'has-error': !isInit.current && hasError
              })}
            >
              <div
                className={classnames('ttcx-modal-input', {
                  'with-max': inputConfig?.maxLength !== undefined
                })}
              >
                <TextArea
                  value={inputValue}
                  onValueChange={onInputChange}
                  placeholder={inputConfig?.placeholder}
                  maxLength={inputConfig?.maxLength}
                  rows={4}
                  {...(inputConfig?.inputProps || {})}
                />
                {inputConfig?.maxLength && (
                  <section className="input-count-area">
                    {inputValue?.length || 0} / {inputConfig?.maxLength}
                  </section>
                )}
              </div>
              {!isInit.current && hasError && <p className="error-text">{errorText}</p>}
            </div>
          )}
        </>
      ) : null}
    </Modal>
  );
}

export default forwardRef(TTCXModal);
